import { formatCurrency, TOKEN_DECIMALS } from "@solomon-apps/shared";
import React from "react";

import { useDistributionsData, useYieldDistributions } from "@/hooks/stats";
import type { ProcessedDistribution } from "@/hooks/stats/useYieldDistributions";
import type { StatsTimePeriod } from "@/pages/Stats/types";

const MS_BY_PERIOD: Record<Exclude<StatsTimePeriod, "all">, number> = {
    "7d": 7 * 86400000,
    "30d": 30 * 86400000,
    "90d": 90 * 86400000,
    "1y": 365 * 86400000
};

/** What the reader sees beside the change, in the tabs' own words. */
const PERIOD_LABELS: Record<StatsTimePeriod, string> = {
    "7d": "1w",
    "30d": "1m",
    "90d": "1q",
    "1y": "1y",
    all: "all time"
};

function filterDistributionsByPeriod(items: ProcessedDistribution[], period: StatsTimePeriod): ProcessedDistribution[] {
    if (period === "all") return items;

    return items.filter((item) => item.timestamp >= Date.now() - MS_BY_PERIOD[period]);
}

export interface ApySeriesPoint {
    timestamp: number;
    apyPct: number;
    /** True for carry-forward points synthesized at the window boundaries. */
    synthetic: boolean;
}

/**
 * Treat APY as the rate in effect between distributions. This keeps sparse
 * windows meaningful: the last rate known at the start anchors the full
 * selected window, real observations replace it as distributions arrive, and
 * the latest rate remains in effect through `now`.
 */
export function buildCarriedApySeries(
    sortedAscending: ReadonlyArray<{ timestamp: number; apyNum: number }>,
    windowStart: number | null,
    now: number
): ApySeriesPoint[] {
    const known = sortedAscending.filter((item) => item.timestamp <= now);
    if (known.length === 0) return [];

    const inWindow = windowStart === null ? known : known.filter((item) => item.timestamp >= windowStart);
    const points: ApySeriesPoint[] = inWindow.map((item) => ({
        timestamp: item.timestamp,
        apyPct: item.apyNum,
        synthetic: false
    }));

    if (windowStart !== null && (points.length === 0 || points[0].timestamp > windowStart)) {
        let priorPoint: (typeof known)[number] | undefined;
        for (let index = known.length - 1; index >= 0; index--) {
            if (known[index].timestamp <= windowStart) {
                priorPoint = known[index];
                break;
            }
        }
        if (priorPoint) {
            points.unshift({
                timestamp: windowStart,
                apyPct: priorPoint.apyNum,
                synthetic: true
            });
        }
    }

    const latestPoint = points[points.length - 1];
    if (latestPoint && latestPoint.timestamp < now) {
        points.push({
            timestamp: now,
            apyPct: latestPoint.apyPct,
            synthetic: true
        });
    }

    return points;
}

function toSparklinePoints(items: ProcessedDistribution[]): number[] {
    if (items.length === 0) return [0, 0];
    if (items.length === 1) return [items[0].apyNum, items[0].apyNum];
    return items.map((item) => item.apyNum);
}

function toSparklineLabels(items: ProcessedDistribution[]): string[] {
    if (items.length === 0) return ["", ""];
    if (items.length === 1) return [items[0].dateShortStr, items[0].dateShortStr];
    return items.map((item) => item.dateShortStr);
}

const shortDateFormatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit"
});

function formatMonthYearLabel(timestamp?: number): string {
    if (!timestamp) return "—";

    const parts = new Intl.DateTimeFormat("en-US", {
        month: "short",
        year: "2-digit"
    }).formatToParts(new Date(timestamp));

    const month = parts.find((part) => part.type === "month")?.value ?? "";
    const year = parts.find((part) => part.type === "year")?.value ?? "";

    return month && year ? `${month} '${year}` : "—";
}

function formatCarriedBoundaryLabel(point: ApySeriesPoint | undefined, isEnd: boolean): string {
    if (!point) return "—";
    if (isEnd && point.synthetic) return "Today";
    return formatMonthYearLabel(point.timestamp);
}

export interface UseYieldDataOptions {
    /** Carry the latest known APY through windows without a distribution. */
    carryForwardApy?: boolean;
}

export interface YieldData {
    apyValue: string;
    /** Signed APY change over the selected period (current − first), or
     *  `null` when the range has no data. Drives `<DeltaBadge>` color +
     *  arrow on the consumer side. */
    apyChange: number | null;
    /** Human-friendly period label paired with `apyChange`
     *  (e.g. "30d", "90d", "1y", "all time"). */
    apyPeriodLabel: string;
    chartPoints: number[];
    /** Timestamps parallel to `chartPoints`, for time-proportional x spacing. */
    chartTimestamps: number[];
    chartLabels: string[];
    /** False only when the selected chart mode has no usable history. */
    hasChartData: boolean;
    /** Complete APY history used by the stats benchmark comparison. */
    allTimeApyHistory: Array<{ timestamp: number; ratePct: number }>;
    /**
     * How many points each range would plot, counted the same way the chart
     * builds them, so a caller can drop a range that has too few to be a line.
     */
    pointsByPeriod: Record<StatsTimePeriod, number>;
    chartStartLabel: string;
    chartEndLabel: string;
    totalDistributed: string;
    totalDistributedNumber: number;
    yieldPerDollar: string;
    recentDistributions: ProcessedDistribution[];
    isLoading: boolean;
}

export function useYieldData(period: StatsTimePeriod, options: UseYieldDataOptions = {}): YieldData {
    const { data: yieldDistributionsData, isLoading: isYieldLoading } = useYieldDistributions(TOKEN_DECIMALS.USDv, 100);
    const { distributions, isLoading: isDistributionsLoading } = useDistributionsData();

    const allYieldDistributions = React.useMemo(
        () => yieldDistributionsData?.items ?? [],
        [yieldDistributionsData?.items]
    );

    const ascendingDistributions = React.useMemo(() => [...allYieldDistributions].reverse(), [allYieldDistributions]);

    const chartDistributions = React.useMemo(
        () => filterDistributionsByPeriod(ascendingDistributions, period),
        [ascendingDistributions, period]
    );

    const recentDistributions = React.useMemo(() => allYieldDistributions.slice(0, 6), [allYieldDistributions]);

    const carriedApySeries = React.useMemo(() => {
        if (!options.carryForwardApy) return [];

        const now = Date.now();
        const windowStart = period === "all" ? null : now - MS_BY_PERIOD[period];
        return buildCarriedApySeries(ascendingDistributions, windowStart, now);
    }, [ascendingDistributions, options.carryForwardApy, period]);

    const chartPoints = React.useMemo(
        () =>
            options.carryForwardApy
                ? carriedApySeries.map((point) => point.apyPct)
                : toSparklinePoints(chartDistributions),
        [carriedApySeries, chartDistributions, options.carryForwardApy]
    );
    const chartTimestamps = React.useMemo(
        () => (options.carryForwardApy ? carriedApySeries.map((point) => point.timestamp) : []),
        [carriedApySeries, options.carryForwardApy]
    );
    const chartLabels = React.useMemo(
        () =>
            options.carryForwardApy
                ? carriedApySeries.map((point, index) =>
                      point.synthetic && index === carriedApySeries.length - 1
                          ? "Today"
                          : shortDateFormatter.format(new Date(point.timestamp))
                  )
                : toSparklineLabels(chartDistributions),
        [carriedApySeries, chartDistributions, options.carryForwardApy]
    );
    const pointsByPeriod = React.useMemo(() => {
        const now = Date.now();
        const periods: StatsTimePeriod[] = ["7d", "30d", "90d", "1y", "all"];
        const counted = periods.map((candidate): [StatsTimePeriod, number] => {
            if (options.carryForwardApy) {
                const windowStart = candidate === "all" ? null : now - MS_BY_PERIOD[candidate];
                return [candidate, buildCarriedApySeries(ascendingDistributions, windowStart, now).length];
            }
            return [candidate, filterDistributionsByPeriod(ascendingDistributions, candidate).length];
        });
        return Object.fromEntries(counted) as Record<StatsTimePeriod, number>;
    }, [ascendingDistributions, options.carryForwardApy]);

    const allTimeApyHistory = React.useMemo(
        () =>
            ascendingDistributions.map((distribution) => ({
                timestamp: distribution.timestamp,
                ratePct: distribution.apyNum
            })),
        [ascendingDistributions]
    );

    const currentApy = options.carryForwardApy
        ? (carriedApySeries[carriedApySeries.length - 1]?.apyPct ?? null)
        : (chartDistributions[chartDistributions.length - 1]?.apyNum ?? null);
    const firstApy = options.carryForwardApy
        ? (carriedApySeries[0]?.apyPct ?? null)
        : (chartDistributions[0]?.apyNum ?? null);
    const apyChange = currentApy !== null && firstApy !== null ? currentApy - firstApy : null;

    const apyValue = currentApy !== null ? `${currentApy.toFixed(1)}%` : "—";
    const apyPeriodLabel = PERIOD_LABELS[period];

    const chartStartLabel = options.carryForwardApy
        ? formatCarriedBoundaryLabel(carriedApySeries[0], false)
        : formatMonthYearLabel(chartDistributions[0]?.timestamp);
    const chartEndLabel = options.carryForwardApy
        ? formatCarriedBoundaryLabel(carriedApySeries[carriedApySeries.length - 1], true)
        : formatMonthYearLabel(chartDistributions[chartDistributions.length - 1]?.timestamp);
    const totalDistributedNumber = distributions?.total ? Number(distributions.total) : 0;
    const totalDistributed = distributions?.total ? formatCurrency(distributions.total) : "—";

    const yieldPerDollarTotal = chartDistributions.reduce((sum, d) => {
        if (d.stakedNum <= 0) return sum;
        return sum + d.yieldNum / d.stakedNum;
    }, 0);
    const yieldPerDollar = yieldPerDollarTotal > 0 ? formatCurrency(yieldPerDollarTotal) : "—";

    return {
        apyValue,
        apyChange,
        apyPeriodLabel,
        chartPoints,
        chartTimestamps,
        chartLabels,
        hasChartData: options.carryForwardApy ? carriedApySeries.length > 0 : chartDistributions.length > 0,
        allTimeApyHistory,
        pointsByPeriod,
        chartStartLabel,
        chartEndLabel,
        totalDistributed,
        totalDistributedNumber,
        yieldPerDollar,
        recentDistributions,
        isLoading: isYieldLoading || isDistributionsLoading
    };
}
